import { useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ArrowUpRight } from "lucide-react";
import {
  Badge,
  Button,
  Combobox,
  FeedbackState,
  Mono,
  MultiSelect,
  SearchInput,
} from "@h2j-data-team/ui";

import { supportCode } from "@/components/error-screen";
import { pickerMessages } from "@/lib/ui-labels";
import { useSearchHotkey } from "@/lib/use-search-hotkey";
import { cn } from "@/lib/utils";
import { fileFormatLabel } from "./qvd-list-utils";

// Sits next to the file name (no column of its own), and only for formats that
// deviate from the QVD norm — a badge on every row says nothing the path doesn't.
export function FileFormatBadge({ path }: { path: string | undefined }) {
  const label = fileFormatLabel(path);
  if (!label || label === "QVD") return null;
  return (
    <Badge variant="outline" className="shrink-0 py-0">
      {label}
    </Badge>
  );
}

// Shared building blocks so the result lists use one consistent pattern
// (toolbar, QVD identifier, space link, load and error states).

// QvdPathLabel renders a QVD by its full path — the folder prefix dimmed, the
// filename emphasized. Replaces the old redundant name+path two-liner.
export function QvdPathLabel({ path, muted }: { path: string; muted?: boolean }) {
  const i = path.lastIndexOf("/");
  const folder = i >= 0 ? path.slice(0, i + 1) : "";
  const name = i >= 0 ? path.slice(i + 1) : path;
  return (
    <div className="truncate font-mono" title={path}>
      {folder && <span className="text-faint">{folder}</span>}
      <span className={cn("font-semibold", muted ? "text-faint" : "text-ink")}>{name}</span>
    </div>
  );
}

// The control a row exposes to open its detail (the cell is marked `rowLink`, so a
// click on the rest of the row reaches it too). A real button: keyboard and
// screen readers use it, not the row.
export function RowButton({
  label,
  onClick,
  children,
}: {
  label: string;
  /** Receives the button, for the drawer or dialog it opens to hand focus back to. */
  onClick: (button: HTMLButtonElement) => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={(event) => onClick(event.currentTarget)}
      className="block max-w-full min-w-0 rounded-sm text-left -outline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-ring"
    >
      {children}
    </button>
  );
}

// SpaceLink renders the space name; when a tenant baseUrl + spaceId are known it
// links to the space's Catalog in Qlik Cloud (new tab).
export function SpaceLink({
  spaceName,
  spaceId,
  baseUrl,
}: {
  spaceName: string | null;
  spaceId?: string | null;
  baseUrl?: string | null;
}) {
  if (!spaceName) return <span className="text-faint">—</span>;
  if (baseUrl && spaceId) {
    const href = `${baseUrl.replace(/\/$/, "")}/catalog?space_filter=${encodeURIComponent(spaceId)}`;
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-0.5 rounded-sm hover:text-ink hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
      >
        {spaceName}
        <ArrowUpRight className="size-3" aria-hidden />
      </a>
    );
  }
  return <span>{spaceName}</span>;
}

// QvdListToolbar is the shared search + space-filter row above the result lists
// (the order lives in the list's column heads). Lists that filter on the server
// take one space at a time (`singleSpace`); the rest filter in memory by several.
export function QvdListToolbar({
  search,
  onSearch,
  searchPlaceholder,
  spaceOptions,
  spaceFilter = [],
  onSpaceFilter,
  singleSpace = false,
  children,
}: {
  search: string;
  onSearch: (v: string) => void;
  searchPlaceholder: string;
  spaceOptions?: string[];
  spaceFilter?: string[];
  onSpaceFilter?: (v: string[]) => void;
  singleSpace?: boolean;
  /** Controls after the filters (a view switch, for instance). */
  children?: ReactNode;
}) {
  const { t } = useTranslation("qvd-analysis");
  const { t: tc } = useTranslation("common");
  const searchRef = useRef<HTMLInputElement>(null);
  useSearchHotkey(searchRef);
  const options = (spaceOptions ?? []).map((s) => ({ label: s, value: s }));
  const messages = pickerMessages({ placeholder: t("filters.allSpaces") });

  return (
    <div className="flex flex-wrap items-center gap-2 pb-3">
      <SearchInput
        ref={searchRef}
        value={search}
        onValueChange={onSearch}
        clearLabel={tc("list.clearSearch")}
        aria-label={searchPlaceholder}
        placeholder={searchPlaceholder}
        shortcutHint="/"
        containerClassName="min-w-60 flex-1 sm:max-w-sm"
      />
      {spaceOptions !== undefined &&
        onSpaceFilter !== undefined &&
        (singleSpace ? (
          <Combobox
            size="compact"
            className="w-48"
            aria-label={t("filters.space")}
            options={options}
            value={spaceFilter[0] ?? null}
            onValueChange={(space) => onSpaceFilter(space ? [space] : [])}
            messages={messages}
            clearable
          />
        ) : (
          <MultiSelect
            size="compact"
            className="w-48"
            aria-label={t("filters.space")}
            options={options}
            value={spaceFilter}
            onValueChange={onSpaceFilter}
            messages={messages}
            maxDisplay={1}
            clearable
          />
        ))}
      {children}
    </div>
  );
}

/** Loading state of a result list, announced by the library's FeedbackState. */
export function ListLoading() {
  const { t } = useTranslation("common");
  return <FeedbackState state="loading" title={t("list.loading")} />;
}

/**
 * Failure of a result list: the translated `title`, a retry, and — on a 5xx —
 * the support code. The server's own message never reaches the screen.
 */
export function ListError({
  error,
  title,
  onRetry,
}: {
  error: unknown;
  title: string;
  onRetry?: () => void;
}) {
  const { t } = useTranslation("common");
  const code = supportCode(error, t("error.supportCode"));
  return (
    <FeedbackState
      state="error"
      title={title}
      description={
        code && (
          <>
            {code.label}: <Mono>{code.value}</Mono>
          </>
        )
      }
      action={
        onRetry && (
          <Button variant="outline" size="sm" onClick={onRetry}>
            {t("tryAgain")}
          </Button>
        )
      }
    />
  );
}
